import { useState, useCallback, useRef } from 'react'
import { Button } from './ui/button'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Badge } from './ui/badge'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select'
import { useToast } from '../hooks/use-toast'
import { api } from '../lib/api-client'
import { Upload, FileImage, AlertTriangle, CheckCircle, XCircle, Pause, Play, Trash2 } from 'lucide-react'
import { useAuth } from 'react-oidc-context'

// Simple progress bar component
const Progress = ({ value, className }) => (
  <div className={`w-full bg-gray-200 rounded-full h-2 ${className}`}>
    <div 
      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
      style={{ width: `${Math.min(Math.max(value || 0, 0), 100)}%` }}
    />
  </div>
)

const CHUNK_SIZE = 10; // Process files in chunks of 10
// const MAX_PREVIEW_FILES = 20; // Only show previews for first 20 files
const MAX_CONCURRENT_UPLOADS = 5; // Max simultaneous S3 uploads

export default function LargeBatchUpload({ matches, onComplete }) {
  const [files, setFiles] = useState([])
  const [uploadState, setUploadState] = useState('idle') // idle, uploading, processing, paused
  const [selectedMatch, setSelectedMatch] = useState('')
  const [currentChunk, setCurrentChunk] = useState(0)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [processingProgress, setProcessingProgress] = useState(0)
  const [failedFiles, setFailedFiles] = useState([])
  const [completedFiles, setCompletedFiles] = useState([])
  const [isPaused, setIsPaused] = useState(false)
  // const [batchId, setBatchId] = useState(null)
  
  const fileInputRef = useRef(null)
  const auth = useAuth()
  const { toast } = useToast()

  // Poll processing progress
  const pollProcessingProgress = useCallback(async (jobId) => {
    const poll = async () => {
      try {
        const status = await api.getBatchJobStatus(jobId)
        if (status.success) {
          const job = status.data
          const progress = job.totalImages > 0 ? 
            ((job.processedImages + job.failedImages) / job.totalImages) * 100 : 0
          
          setProcessingProgress(progress)

          if (job.status === 'completed' || job.status === 'failed') {
            setUploadState('idle')
            if (onComplete) onComplete()
            return
          }

          // Continue polling
          setTimeout(poll, 2000)
        }
      } catch (error) {
        console.error('Polling error:', error)
        setTimeout(poll, 5000) // Retry with longer delay
      }
    }
    
    poll()
  }, [onComplete])

  // Start batch processing
  const startBatchProcessing = useCallback(async (uploadedFiles) => {
    setUploadState('processing')
    setProcessingProgress(0)

    try {
      const batchResponse = await api.uploadBatch({
        s3Keys: uploadedFiles.map(f => f.s3Key),
        metadata: {
          matchId: selectedMatch,
          uploader: auth.isAuthenticated && auth.user && auth.user.profile ? auth.user.profile.email : 'system',
          uploadedBy: auth.isAuthenticated && auth.user && auth.user.profile ? auth.user.profile.email : 'system'
        }
      })

      if (batchResponse.success) {
        // Start polling for progress
        pollProcessingProgress(batchResponse.data.jobId)
      }
    } catch (error) {
      console.error('Failed to start batch processing:', error)
      toast({
        title: "Processing Failed",
        description: "Failed to start image analysis.",
        variant: "destructive",
      })
    }
  }, [selectedMatch, toast, pollProcessingProgress, auth.isAuthenticated, auth.user])

  // File selection with memory optimization
  const handleFileSelect = useCallback((selectedFiles) => {
    const imageFiles = Array.from(selectedFiles).filter(file => 
      file.type.startsWith('image/')
    )

    if (imageFiles.length === 0) {
      toast({
        title: "No Valid Images",
        description: "Please select image files only.",
        variant: "destructive",
      })
      return
    }

    if (imageFiles.length > 100) {
      toast({
        title: "Too Many Files",
        description: `Selected ${imageFiles.length} files. Maximum 100 files per batch.`,
        variant: "destructive",
      })
      return
    }

    setFiles(imageFiles)
    toast({
      title: "Files Selected",
      description: `Ready to upload ${imageFiles.length} images.`,
    })
  }, [toast])

  // Chunked upload with concurrent S3 uploads
  const uploadFilesInChunks = useCallback(async () => {
    if (!selectedMatch) {
      toast({
        title: "Match Required",
        description: "Please select a match before uploading images. All images in this batch will be assigned to the selected match.",
        variant: "destructive",
        duration: 5000,
      })
      return
    }

    // Prevent multiple simultaneous uploads
    if (uploadState !== 'idle') {
      return
    }

    setUploadState('uploading')
    setCurrentChunk(0)
    setUploadProgress(0)
    setFailedFiles([])
    setCompletedFiles([])

    try {
      const totalChunks = Math.ceil(files.length / CHUNK_SIZE)
      let allUploadedFiles = []
      let allFailedFiles = []

      for (let chunkIndex = 0; chunkIndex < totalChunks && !isPaused; chunkIndex++) {
        setCurrentChunk(chunkIndex + 1)
        const chunkStart = chunkIndex * CHUNK_SIZE
        const chunkFiles = files.slice(chunkStart, chunkStart + CHUNK_SIZE)

        try {
          // Get presigned URLs for this chunk with retry logic
          let urlsResponse
          let retryCount = 0
          const maxRetries = 3
          
          while (retryCount < maxRetries) {
            try {
              urlsResponse = await api.generateUploadUrls({
                files: chunkFiles.map(file => ({
                  filename: file.name,
                  filetype: file.type
                }))
              })
              break // Success, exit retry loop
            } catch (error) {
              retryCount++
              console.error(`Generate URLs attempt ${retryCount} failed:`, error.message)
              if (retryCount >= maxRetries) throw error
              const delay = 1000 * Math.min(retryCount, 3)
              await new Promise(resolve => setTimeout(resolve, delay)) // Use consistent backoff
            }
          }

          if (!urlsResponse.success) {
            throw new Error('Failed to get presigned URLs')
          }

          // Debug: Check presigned URL response structure
          // console.log('Presigned URLs response:', {
          //   success: urlsResponse.success,
          //   dataType: typeof urlsResponse.data,
          //   dataLength: Array.isArray(urlsResponse.data) ? urlsResponse.data.length : 'not array',
          //   firstItem: urlsResponse.data?.[0]
          // })

          // Upload files to S3 with concurrency control
          const uploadResults = await uploadChunkToS3(chunkFiles, urlsResponse.data)
          const successful = uploadResults.filter(r => r.success)
          const failed = uploadResults.filter(r => !r.success)

          allUploadedFiles.push(...successful)
          allFailedFiles.push(...failed)

          // Update progress
          const totalProcessed = (chunkIndex + 1) * CHUNK_SIZE
          setUploadProgress(Math.min((totalProcessed / files.length) * 100, 100))
          
          setCompletedFiles(allUploadedFiles)
          setFailedFiles(allFailedFiles)

        } catch (error) {
          allFailedFiles.push(...chunkFiles.map(file => ({ file, error: error.message })))
        }

        // Brief pause between chunks to prevent overwhelming
        if (chunkIndex < totalChunks - 1) {
          await new Promise(resolve => setTimeout(resolve, 500))
        }
      }

      if (isPaused) {
        toast({
          title: "Upload Paused",
          description: `Upload paused after ${allUploadedFiles.length} files.`,
        })
        return
      }

      // Start batch processing if any files uploaded successfully
      if (allUploadedFiles.length > 0) {
        await startBatchProcessing(allUploadedFiles)
      }

      // Show final results
      toast({
        title: "Upload Complete",
        description: `${allUploadedFiles.length} files uploaded successfully. ${allFailedFiles.length} failed.`,
        variant: allFailedFiles.length === 0 ? "default" : "destructive",
      })

    } catch (error) {
      console.error('Batch upload failed:', error)
      setUploadState('idle')
      toast({
        title: "Upload Failed",
        description: error.message,
        variant: "destructive",
      })
    }
  }, [files, selectedMatch, isPaused, toast, startBatchProcessing, uploadState])

  // Upload chunk to S3 with concurrency control
  const uploadChunkToS3 = async (chunkFiles, presignedUrls) => {
    const uploadPromises = chunkFiles.map(async (file, index) => {
      try {
        const urlData = presignedUrls[index]
        
        // Check if urlData is valid
        if (!urlData) {
          throw new Error(`No presigned URL data for file ${file.name}`)
        }
        if (!urlData.url) {
          throw new Error(`Missing URL in presigned data for file ${file.name}`)
        }
        if (!urlData.s3Key) {
          throw new Error(`Missing s3Key in presigned data for file ${file.name}`)
        }
        
        // Use PUT request like standard upload (not POST with form data)
        const response = await fetch(urlData.url, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type
          }
        })

        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Unknown error');
          console.error(`S3 upload failed for ${file.name}:`, {
            status: response.status,
            statusText: response.statusText,
            errorBody: errorText,
            presignedUrl: urlData.url.split('?')[0] // Log URL without sensitive query params
          });
          throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`)
        }

        return {
          success: true,
          file,
          s3Key: urlData.s3Key,
          filename: file.name
        }
      } catch (error) {
        console.error(`Upload failed for ${file.name}:`, error)
        return {
          success: false,
          file,
          error: error.message
        }
      }
    })

    // Control concurrency
    const results = []
    for (let i = 0; i < uploadPromises.length; i += MAX_CONCURRENT_UPLOADS) {
      const chunk = uploadPromises.slice(i, i + MAX_CONCURRENT_UPLOADS)
      const chunkResults = await Promise.all(chunk)
      results.push(...chunkResults)
    }

    return results
  }

  const handlePauseResume = () => {
    setIsPaused(!isPaused)
    if (isPaused) {
      toast({ title: "Upload Resumed" })
    } else {
      toast({ title: "Upload Paused" })
    }
  }

  const handleCancel = () => {
    setUploadState('idle')
    setFiles([])
    setIsPaused(false)
    setCurrentChunk(0)
    setUploadProgress(0)
    setProcessingProgress(0)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const retryFailedFiles = () => {
    if (failedFiles.length > 0) {
      setFiles(failedFiles.map(f => f.file))
      setFailedFiles([])
      uploadFilesInChunks()
    }
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Large Batch Upload
          <Badge variant="secondary">{files.length} files</Badge>
        </CardTitle>
        <p className="text-sm text-gray-600 mt-2">
          Upload multiple images efficiently. Files are processed in chunks and analyzed by AI to detect sponsors and identify players.
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
          <p className="text-sm text-blue-800">
            <strong>⏱️ Processing Time:</strong> Images are queued for AI analysis at 4 seconds per image (API rate limits). 
            Expect ~4 minutes for 60 images. Processing happens reliably in the background.
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        
        {/* File Selection */}
        <div>
          <Label 
            htmlFor="batch-files" 
            title="Select up to 100 images for batch upload. Files will be uploaded in chunks of 10 for optimal performance."
          >
            Select Images (max 100)
          </Label>
          <Input
            ref={fileInputRef}
            id="batch-files"
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => handleFileSelect(e.target.files)}
            disabled={uploadState !== 'idle'}
            className="mt-1"
            title="Choose multiple image files. Large batch mode processes files in background for better performance."
          />
        </div>

        {/* Match Selection */}
        {files.length > 0 && (
          <div>
            <Label 
              htmlFor="match-select"
              title="All images in this batch will be assigned to the selected match. This helps organize your images by game."
            >
              Assign to Match *
            </Label>
            <Select value={selectedMatch} onValueChange={setSelectedMatch}>
              <SelectTrigger title="Choose which match these images belong to. Required before upload.">
                <SelectValue placeholder="Select a match" />
              </SelectTrigger>
              <SelectContent>
                {matches.map(match => (
                  <SelectItem key={match.id} value={match.id}>
                    {match.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Upload Progress */}
        {uploadState === 'uploading' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Uploading Chunk {currentChunk}</span>
              <span className="text-sm text-muted-foreground">{Math.round(uploadProgress)}%</span>
            </div>
            <Progress value={uploadProgress} className="w-full" title="Files are uploaded to cloud storage in chunks for reliability" />
            <p className="text-xs text-gray-500">
              Files are being uploaded to secure cloud storage. After upload, AI analysis will begin automatically.
            </p>
          </div>
        )}

        {/* Processing Progress */}
        {uploadState === 'processing' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">AI Analysis in Progress</span>
              <span className="text-sm text-muted-foreground">{Math.round(processingProgress)}%</span>
            </div>
            <Progress value={processingProgress} className="w-full" title="AI is analyzing images to detect sponsors and identify players" />
            <p className="text-xs text-gray-500">
              AI is analyzing each image to detect sponsor logos and identify Shamrock Rovers players. 
              Each image takes ~4 seconds due to API rate limits. Processing is queued reliably.
            </p>
          </div>
        )}

        {/* Status Summary */}
        {files.length > 0 && (
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-3 bg-blue-50 rounded-lg">
              <FileImage className="h-6 w-6 mx-auto mb-1 text-blue-600" />
              <div className="text-sm font-medium">{files.length}</div>
              <div className="text-xs text-muted-foreground">Total</div>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <CheckCircle className="h-6 w-6 mx-auto mb-1 text-green-600" />
              <div className="text-sm font-medium">{completedFiles.length}</div>
              <div className="text-xs text-muted-foreground">Completed</div>
            </div>
            <div className="p-3 bg-red-50 rounded-lg">
              <XCircle className="h-6 w-6 mx-auto mb-1 text-red-600" />
              <div className="text-sm font-medium">{failedFiles.length}</div>
              <div className="text-xs text-muted-foreground">Failed</div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          {uploadState === 'idle' && (
            <Button 
              onClick={uploadFilesInChunks}
              disabled={files.length === 0 || !selectedMatch}
              className="flex-1"
              title={
                files.length === 0 
                  ? "Select images first" 
                  : !selectedMatch 
                    ? "Choose a match first" 
                    : `Upload ${files.length} images and start AI analysis`
              }
            >
              <Upload className="h-4 w-4 mr-2" />
              Start Upload
            </Button>
          )}
          
          {uploadState === 'uploading' && (
            <>
              <Button 
                onClick={handlePauseResume}
                variant="outline"
                className="flex-1"
              >
                {isPaused ? <Play className="h-4 w-4 mr-2" /> : <Pause className="h-4 w-4 mr-2" />}
                {isPaused ? 'Resume' : 'Pause'}
              </Button>
              <Button 
                onClick={handleCancel}
                variant="destructive"
                className="flex-1"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </>
          )}

          {failedFiles.length > 0 && uploadState === 'idle' && (
            <Button 
              onClick={retryFailedFiles}
              variant="outline"
              className="flex-1"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Retry Failed ({failedFiles.length})
            </Button>
          )}
        </div>

        {/* Failed Files List */}
        {failedFiles.length > 0 && (
          <div className="mt-4">
            <Label>Failed Files:</Label>
            <div className="mt-2 max-h-32 overflow-y-auto border rounded p-2 bg-red-50">
              {failedFiles.map((failed, index) => (
                <div key={index} className="text-sm text-red-700 py-1">
                  {failed.file.name}: {failed.error}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}